import axios from 'axios';
import { createNote, uploadImageAsNote, getNoteAttachment } from "./noteService";

const API_URL = "https://seltentechnologiesdemo.com/moona-backend/service/v4_1/rest.php";
const MODULE_NAME = "work_work";
const INPUT_TYPE = "JSON";
const RESPONSE_TYPE = "JSON";

// Fields exposed in the Work Experience UI and available in the SuiteCRM module
const WORK_MODULE_FIELDS = [
  "id", // Required for edit operations
  "address",
  "city",
  "companyname",
  "country",
  "currentlyworking",
  "date_entered",
  "date_modified",
  "deleted",
  "department",
  "description",
  "designation",
  "employmenttype",
  "enddate",
  "name",
  "parentid",
  "pincode",
  "sector",
  "startdate",
  "state",
  "image",
];

// Map UI field aliases to SuiteCRM fields where names differ (if any)
const UI_FIELD_ALIASES = {
  // Add any field aliases if needed
};

// Filter outgoing payload to include only module fields used in the UI
const filterWorkPayload = (data = {}) => {
  const payload = {};

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    const normalizedValue = typeof value === "string" ? value.trim() : value;

    if (normalizedValue === "") {
      // allow empty strings for explicit clearing only when field explicitly present
      if (!Object.prototype.hasOwnProperty.call(data, key)) {
        return;
      }
    }

    if (
      key === "image" &&
      typeof normalizedValue === "string" &&
      normalizedValue.startsWith("data:")
    ) {
      // Skip base64 data URI values; handled separately via notes
      return;
    }

    if (WORK_MODULE_FIELDS.includes(key)) {
      payload[key] = normalizedValue;
      return;
    }

    const mappedField = UI_FIELD_ALIASES[key];
    if (mappedField && WORK_MODULE_FIELDS.includes(mappedField)) {
      payload[mappedField] = normalizedValue;
    }
  });

  return payload;
};

// Helper function to process work experience data entries
const processWorkData = async (entryList, sessionId) => {
  if (!Array.isArray(entryList)) return [];

  const processedEntries = entryList.map((entry) => {
    const values = Object.fromEntries(
      Object.values(entry.name_value_list || {}).map((field) => [field.name, field.value])
    );

    const filteredValues = {};
    WORK_MODULE_FIELDS.forEach((field) => {
      if (values[field] !== undefined && values[field] !== null) {
        filteredValues[field] = values[field];
      }
    });

    // Ensure alias fields are also surfaced for UI convenience
    Object.entries(UI_FIELD_ALIASES).forEach(([uiField, moduleField]) => {
      if (filteredValues[moduleField] !== undefined) {
        filteredValues[uiField] = filteredValues[moduleField];
      }
    });

    return {
      id: entry.id,
      ...filteredValues,
    };
  });

  if (sessionId) {
    for (const entry of processedEntries) {
      if (entry.image && typeof entry.image === "string" && !entry.image.startsWith("data:")) {
        try {
          const imageData = await getNoteAttachment(sessionId, entry.image);
          if (imageData) {
            entry.image = imageData;
          }
        } catch (error) {
          console.error("Error fetching work experience image note attachment:", error);
        }
      }
    }
  }

  return processedEntries;
};

const uploadWorkExperienceImage = async (sessionId, workEntryId, base64Image) => {
  if (!sessionId || !workEntryId || !base64Image || !base64Image.startsWith("data:")) {
    return null;
  }

  try {
    const noteName = "Work Experience ID Card Image";
    const noteId = await createNote(sessionId, MODULE_NAME, workEntryId, noteName);
    const filename = `work_experience_${Date.now()}.jpg`;
    await uploadImageAsNote(sessionId, noteId, base64Image, filename, MODULE_NAME, workEntryId);
    return noteId;
  } catch (error) {
    console.error("Error uploading work experience image:", error);
    throw error;
  }
};

/**
 * Fetch all work experience data from Work module
 * @param {string} sessionId - SuiteCRM session ID
 * @returns {Promise<Array>} - Array of work experience objects
 */
export const fetchWorkData = async (sessionId) => {
  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      select_fields: WORK_MODULE_FIELDS,
    });

    const fullUrl = `${API_URL}?method=get_entry_list&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.get(fullUrl);
    const entryList = response?.data?.entry_list || [];

    return await processWorkData(entryList, sessionId);
  } catch (error) {
    console.error('Error fetching work experience data:', error);
    throw new Error('Failed to fetch work experience data');
  }
};

/**
 * Fetch work experience entries for a specific parent/user
 * @param {string} sessionId
 * @param {string} parentId
 * @returns {Promise<Array>} - Array of work experience entries associated with the parent
 */
export const fetchWorkDetailsByParent = async (sessionId, parentId) => {
  if (!sessionId || !parentId) return [];

  try {
    const rest_data = JSON.stringify({
      session: sessionId,
  module_name: MODULE_NAME,
      query: `work_work.parentid = '${parentId}' AND work_work.deleted = 0`,
      order_by: "date_entered",
      offset: 0,
      select_fields: WORK_MODULE_FIELDS,
      max_results: 200,
    });

    const fullUrl = `${API_URL}?method=get_entry_list&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;
    const response = await axios.get(fullUrl);
    const entryList = response?.data?.entry_list || [];

    return await processWorkData(entryList, sessionId);
  } catch (error) {
    console.error('Error fetching work experience data by parent:', error);
    return [];
  }
};

/**
 * Add new work experience entry
 * @param {string} sessionId - SuiteCRM session ID
 * @param {Object} data - Work experience data object
 * @returns {Promise<Object>} - Created entry data
 */
export const addWorkData = async (sessionId, data) => {
  try {
    const payloadSource = data || {};
    const { image } = payloadSource;
    const filteredPayload = filterWorkPayload(payloadSource);

    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: Object.entries(filteredPayload).map(([key, value]) => ({
        name: key,
        value: value
      }))
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.post(fullUrl);
    const createdId = response?.data?.id;

    if (createdId && image && typeof image === "string" && image.startsWith("data:")) {
      const noteId = await uploadWorkExperienceImage(sessionId, createdId, image);
      if (noteId) {
        const updatePayload = filterWorkPayload({ ...payloadSource, id: createdId, image: noteId });
        updatePayload.id = createdId;

        const updateRestData = JSON.stringify({
          session: sessionId,
          module_name: MODULE_NAME,
          name_value_list: Object.entries(updatePayload).map(([key, value]) => ({
            name: key,
            value,
          })),
        });

        const updateUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(updateRestData)}`;
        await axios.post(updateUrl);
      }
    }

    return response.data;
  } catch (error) {
    console.error('Error adding work experience data:', error);
    throw new Error('Failed to add work experience data');
  }
};

/**
 * Edit existing work experience entry
 * @param {string} sessionId - SuiteCRM session ID
 * @param {string} id - Entry ID to update
 * @param {Object} data - Updated work experience data object
 * @returns {Promise<Object>} - Updated entry data
 */
export const editWorkData = async (sessionId, id, data) => {
  try {
    // Include the id in the data object before filtering
    const dataWithId = { ...data, id };
    const updatedData = { ...dataWithId };

    if (data && typeof data.image === "string" && data.image.startsWith("data:")) {
      const noteId = await uploadWorkExperienceImage(sessionId, id, data.image);
      updatedData.image = noteId;
    }

    const filteredPayload = filterWorkPayload(updatedData);
    filteredPayload.id = id;

    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: Object.entries(filteredPayload).map(([key, value]) => ({
        name: key,
        value: value
      }))
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.post(fullUrl);
    return response.data;
  } catch (error) {
    console.error('Error editing work experience data:', error);
    throw new Error('Failed to edit work experience data');
  }
};

/**
 * Delete work experience entry
 * @param {string} sessionId - SuiteCRM session ID
 * @param {string} id - Entry ID to delete
 * @returns {Promise<Object>} - Deletion result
 */
export const deleteWorkData = async (sessionId, id) => {
  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: [
        { name: "id", value: id },
        { name: "deleted", value: 1 },
      ],
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.post(fullUrl);

    if (response?.data?.id !== id) {
      console.warn(`Unexpected delete response for work experience entry ${id}:`, response?.data);
    }

    return response.data;
  } catch (error) {
    console.error('Error deleting work experience data:', error);
    throw new Error('Failed to delete work experience data');
  }
};

/**
 * Synchronize work experience entries for a user with SuiteCRM
 * @param {string} sessionId
 * @param {string} parentId
 * @param {Array} entries - Work experience entries prepared from UI data
 * @param {Array} removedIds - IDs of entries to remove
 */
export const syncWorkEntries = async (sessionId, parentId, entries = [], removedIds = []) => {
  if (!sessionId || !parentId) return;

  const sanitizedEntries = Array.isArray(entries) ? entries : [];
  const removalSet = new Set((removedIds || []).filter(Boolean));

  const existingEntries = await fetchWorkDetailsByParent(sessionId, parentId);
  const existingById = new Map(existingEntries.map((entry) => [entry.id, entry]));
  const existingByName = new Map(
    existingEntries
      .filter((entry) => entry.name)
      .map((entry) => [entry.name.toLowerCase(), entry])
  );

  const usedIds = new Set();

  for (const sourceEntry of sanitizedEntries) {
    if (!sourceEntry || typeof sourceEntry !== "object") continue;

    const prepared = {
      ...sourceEntry,
      parentid: parentId,
    };

    const filteredPayload = filterWorkPayload(prepared);

    const payloadKeys = Object.keys(filteredPayload);
    const hasData = payloadKeys.some(
      (key) => key !== "parentid" && filteredPayload[key] !== undefined && filteredPayload[key] !== null && filteredPayload[key] !== ""
    );
    if (!hasData) continue;

    const nameValueList = Object.entries(filteredPayload).map(([key, value]) => ({
      name: key,
      value: value,
    }));

    let targetId = sourceEntry.id || undefined;

    if (!targetId && sourceEntry.name) {
      const match = existingByName.get(sourceEntry.name.toLowerCase());
      if (match) {
        targetId = match.id;
      }
    }

    if (targetId && existingById.has(targetId)) {
      nameValueList.push({ name: "id", value: targetId });
    }

    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: MODULE_NAME,
      name_value_list: nameValueList,
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;
    const response = await axios.post(fullUrl);
    const savedId = response?.data?.id || targetId;

    if (savedId) {
      usedIds.add(savedId);
    }

    const shouldUploadImage =
      !!savedId &&
      typeof sourceEntry.image === "string" &&
      sourceEntry.image.startsWith("data:");

    if (shouldUploadImage) {
      try {
        const noteId = await uploadWorkExperienceImage(sessionId, savedId, sourceEntry.image);
        if (noteId) {
          const updateRestData = JSON.stringify({
            session: sessionId,
            module_name: MODULE_NAME,
            name_value_list: [
              { name: "id", value: savedId },
              { name: "image", value: noteId },
            ],
          });

          const updateUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(updateRestData)}`;
          await axios.post(updateUrl);
        }
      } catch (error) {
        console.error(`Failed to upload work experience image for entry ${savedId}:`, error);
      }
    }
  }

  // Soft delete entries that were removed in the UI
  const entriesToDelete = existingEntries.filter((entry) => {
    if (!entry.id) return false;
    if (removalSet.has(entry.id)) return true;
    return !usedIds.has(entry.id);
  });

  for (const entry of entriesToDelete) {
    try {
      await deleteWorkData(sessionId, entry.id);
    } catch (error) {
      console.error(`Failed to delete work experience entry ${entry.id}:`, error);
    }
  }
};
